import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { contentCountsUpToDate } from '~src/spacesPlatform/actions/ContentCountsActions';
import { enterSpace, setSpaceInfoItem } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { setOrderAction } from '~src/spacesPlatform/actions/SetOrderAction';
import { updateBreadcrumbs } from '~src/spacesPlatform/actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbStateInfo';
import { getOrderClausesFromQueryParam } from '~src/spacesPlatform/components/Explorer/ExplorerUrlBindings';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { GroupSubspaceName, groupSubspaceNameToExplorableItemType, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../../../../app/spaces/RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../../../../app/spaces/RouterHelpers/RouterError';
import { UrlParamKeys } from '../../../../app/UrlParamKeys';
import { MapDispatchToProps } from '../../../../app/utils/ConnectUtils';
import { Messages } from '../../../../codegen/Localize';
import { ExplorableItemType } from '../../../../spacesPlatform/models/ContentType';
import { IGetGroupDetailInfoResult, IGroupInfo } from '../../../../ts/models/IGroupInfo';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { Groups } from '../../Groups';
import { ConnectedGroupSpace } from './ConnectedGroupSpace';
import { GroupSpaceOwnProps } from './GroupSpace';

export interface GroupSpaceProviderOwnProps {
  readonly siteName: string;
  readonly subspaceName: GroupSubspaceName;
  readonly groupId: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (props: GroupSpaceOwnProps) => JSX.Element;
}

export interface GroupSpaceProviderStateProps {}

export interface GroupSpaceProviderDispatchProps {
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly contentCountsUpToDate: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
}

export interface LocationProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface GroupSpaceProviderState {
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly groupInfo: IGroupInfo;
  readonly groupDetailsInfo: IGetGroupDetailInfoResult;
}

const InitialState: GroupSpaceProviderState = {
  initialRender: true,
  renderChildren: false,
  hasError: false,
  groupInfo: undefined,
  groupDetailsInfo: undefined
};

export type GroupSpaceProviderProps = LocationProps & Omit<GroupSpaceProviderOwnProps, 'renderSpace'>;

export function GroupSpaceProvider(props: GroupSpaceProviderProps) {
  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateFilters: true,
    shouldUpdateContentTypes: false,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: false
  };

  const spaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'group',
    subspaceName: props.subspaceName
  };

  const spaceProps = {
    siteName: props.siteName,
    groupId: props.groupId,
    subspaceName: props.subspaceName,
    routeLocationSearch: props.routeLocationSearch,
    urls: props.urls,
    renderSpace
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedGroupSpaceProviderContainer {...spaceProps} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
}

export type GroupSpaceProviderContainerProps = GroupSpaceProviderOwnProps & GroupSpaceProviderStateProps & GroupSpaceProviderDispatchProps;

export class GroupSpaceProviderContainer extends React.PureComponent<GroupSpaceProviderContainerProps, GroupSpaceProviderState> {

  private unmounted: boolean = false;

  public constructor(props: GroupSpaceProviderContainerProps) {
    super(props);
    this.state = InitialState;
  }

  public render() {
    if (this.state.renderChildren && this.haveAllDataNeededToRender()) {
      const props: GroupSpaceOwnProps = {
        urls: this.props.urls,
        reloadSpace: this.reloadSpace,
      };
      return this.props.renderSpace(props);
    } else if (this.state.hasError) {
      return <RouterError errorType={'group'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentDidMount(){
    this.getData();
  }

  public componentDidUpdate(prevProps: GroupSpaceProviderContainerProps) {
    if (this.changedGroup(prevProps)) {
      this.updateState(InitialState /* initial state for new group */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace();
      this.setState({initialRender: false, renderChildren: true});
    } else if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private changeSubspace() {
    this.setSpaceInfoAndEnterSpace();
    this.props.contentCountsUpToDate();

    if (this.props.subspaceName === 'details') {
      return;
    }

    this.clearAndSetSortOrderFromURL();

    this.props.requestSpaceContents();

  }

  private setSpaceInfoAndEnterSpace() {
    if (this.props.subspaceName === 'details') {
      this.props.enterSpace (
        {
          item: this.state.groupDetailsInfo,
          spaceType: 'group',
          subspaceName: this.props.subspaceName
        }
      );
    } else {
      this.props.enterSpace(
        {
          item: this.state.groupInfo,
          spaceType: 'group',
          subspaceName: this.props.subspaceName
        }
      );
    }
  }

  private clearAndSetSortOrderFromURL() {
    const contentTypes = [groupSubspaceNameToExplorableItemType(this.props.subspaceName) as ExplorableItemType];
    const spaceInfo = {item: undefined, spaceType: 'group', subspaceName: this.props.subspaceName} as SpaceInfo;
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private changedGroup(prevProps: GroupSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.groupId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.groupId !== this.props.groupId);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.groupInfo);
  }

  private getData = async () => {
    try {
      let [group, groupDetailsInfo] = await Promise.all([Groups.getById(this.props.groupId), Groups.getGroupDetails(this.props.groupId)]);
      this.updateState({groupInfo: group, groupDetailsInfo: groupDetailsInfo});
      this.props.updateBreadcrumbs([getGroupsPlaceBreadcrumb(this.props.urls), {text: group.name}]);
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private updateState<K extends keyof GroupSpaceProviderState>(newState: Pick<GroupSpaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private reloadSpace = async () => {
    await this.getData();
    if (this.props.subspaceName === 'users') {
      this.props.setSpaceInfoItem(
        {
          item: this.state.groupInfo,
          spaceType: 'group',
          subspaceName: this.props.subspaceName
        }
      );
    } else {
      this.props.setSpaceInfoItem(
        {
          item: this.state.groupDetailsInfo,
          spaceType: 'group',
          subspaceName: this.props.subspaceName
        }
      );
    }
  }
}

const renderSpace = (props: GroupSpaceOwnProps): JSX.Element => <ConnectedGroupSpace {...props} />;

const mapDispatchToProps: MapDispatchToProps<GroupSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setSpaceInfoItem,
  requestSpaceContents,
  enterSpace,
  contentCountsUpToDate,
  setOrderAction,
  updateBreadcrumbs,
}, dispatch);

export const ConnectedGroupSpaceProviderContainer: React.ComponentClass<GroupSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(GroupSpaceProviderContainer);

export function getGroupsPlaceBreadcrumb(urls: SpacesSiteUrls): BreadcrumbStateInfo {
  const href = urls.groupsUrl();
  return {
    id: href,
    text: Messages.allGroups(),
    href: href,
    testId: 'groups-link'
  };
}
